import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { leadsApi, type OpdReportDrillMetric, type LeadOpdReport } from "@/api/leads";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FilterMultiSelect } from "@/components/ui/filter-multi-select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { shouldMaskLeadPhone } from "@/utils/crmAccess";
import { maskPhoneDisplay } from "@/utils/stringUtils";
import { formatLeadStatusLabel, formatLeadSubStatusLabel } from "@/lib/crmLeadStyles";
import { LeadDetailModal } from "@/components/crm/LeadDetailModal";
import type { Lead } from "@/api/leads";
import {
  EXITED_EMPLOYEES_BUCKET_ID,
  EXITED_EMPLOYEES_BUCKET_LABEL,
} from "@/constants/crmExitedEmployees";

type Props = {
  from: string;
  to: string;
  hospitalId?: string;
};

const DEFAULT_SOURCES = [
  "GOOGLE_PAID",
  "GMB",
  "META",
  "META_PAID",
  "WHATSAPP_INSTA_ADS",
  "WHATSAPP",
  "PRACTO",
  "HEXA",
  "REFERRAL",
  "WALK_IN",
  "OFFLINE",
  "OTHER",
  "EXOTEL",
  "ORGANIC_DIRECT",
  "ORGANIC_GOOGLE",
  "DIRECT_LANDING",
  "INSTAGRAM_ORGANIC",
];

const METRIC_LABELS: Record<OpdReportDrillMetric, string> = {
  booked_online_free: "OPD booked · Online · Free",
  booked_online_paid: "OPD booked · Online · Paid",
  booked_offline_free: "OPD booked · Offline · Free",
  booked_offline_paid: "OPD booked · Offline · Paid",
  visited_online_free: "OPD visited · Online · Free",
  visited_online_paid: "OPD visited · Online · Paid",
  visited_offline_free: "OPD visited · Offline · Free",
  visited_offline_paid: "OPD visited · Offline · Paid",
  status_online_not_visited: "OPD status · Online · Not visited",
  status_offline_not_visited: "OPD status · Offline · Not visited",
  status_online_rescheduled: "OPD status · Online · Rescheduled",
  status_offline_rescheduled: "OPD status · Offline · Rescheduled",
  status_online_lost: "OPD status · Online · Lost",
  status_offline_lost: "OPD status · Offline · Lost",
};

type DrillState = {
  metric: OpdReportDrillMetric;
  staffId?: string;
  repLabel: string;
};

function CountCell({
  value,
  metric,
  staffId,
  repLabel,
  onDrill,
}: {
  value: number;
  metric: OpdReportDrillMetric;
  staffId?: string;
  repLabel: string;
  onDrill: (d: DrillState) => void;
}) {
  if (value <= 0) {
    return <span className="tabular-nums text-muted-foreground">0</span>;
  }
  return (
    <button
      type="button"
      className="tabular-nums font-medium text-indigo-700 hover:underline"
      onClick={() => onDrill({ metric, staffId, repLabel })}
    >
      {value}
    </button>
  );
}

function SplitCells({
  split,
  prefix,
  staffId,
  repLabel,
  onDrill,
}: {
  split: { online: { free: number; paid: number }; offline: { free: number; paid: number } };
  prefix: "booked" | "visited";
  staffId?: string;
  repLabel: string;
  onDrill: (d: DrillState) => void;
}) {
  return (
    <>
      <td className="p-2 text-right">
        <CountCell
          value={split.online.free}
          metric={`${prefix}_online_free` as OpdReportDrillMetric}
          staffId={staffId}
          repLabel={repLabel}
          onDrill={onDrill}
        />
      </td>
      <td className="p-2 text-right">
        <CountCell
          value={split.online.paid}
          metric={`${prefix}_online_paid` as OpdReportDrillMetric}
          staffId={staffId}
          repLabel={repLabel}
          onDrill={onDrill}
        />
      </td>
      <td className="p-2 text-right">
        <CountCell
          value={split.offline.free}
          metric={`${prefix}_offline_free` as OpdReportDrillMetric}
          staffId={staffId}
          repLabel={repLabel}
          onDrill={onDrill}
        />
      </td>
      <td className="p-2 text-right">
        <CountCell
          value={split.offline.paid}
          metric={`${prefix}_offline_paid` as OpdReportDrillMetric}
          staffId={staffId}
          repLabel={repLabel}
          onDrill={onDrill}
        />
      </td>
    </>
  );
}

export function LeadOpdReportTab({ from, to, hospitalId }: Props) {
  const { user } = useAuth();
  const maskPhone = shouldMaskLeadPhone(user);
  const [drill, setDrill] = useState<DrillState | null>(null);
  const [detailLead, setDetailLead] = useState<Lead | null>(null);
  const [rangeFrom, setRangeFrom] = useState(from);
  const [rangeTo, setRangeTo] = useState(to);
  const [sourceFilters, setSourceFilters] = useState<string[]>([]);

  useEffect(() => {
    setRangeFrom(from);
    setRangeTo(to);
  }, [from, to]);

  const { data: filterOptions } = useQuery({
    queryKey: ["lead-filter-options", hospitalId],
    queryFn: () => leadsApi.getLeadFilterOptions(hospitalId ? { hospitalId } : undefined),
  });

  const sourceFilterOptions = useMemo(() => {
    const fromApi = filterOptions?.sources ?? [];
    const merged = new Map<string, string>();
    for (const s of [...fromApi, ...DEFAULT_SOURCES]) {
      const t = String(s).trim();
      if (t) merged.set(t.toUpperCase(), t);
    }
    return [...merged.values()]
      .sort((a, b) => a.localeCompare(b))
      .map((s) => ({ value: s, label: s }));
  }, [filterOptions?.sources]);

  const params = useMemo(() => {
    const p: { from: string; to: string; hospitalId?: string; source?: string } = {
      from: rangeFrom,
      to: rangeTo,
    };
    if (hospitalId) p.hospitalId = hospitalId;
    if (sourceFilters.length) p.source = sourceFilters.join(",");
    return p;
  }, [rangeFrom, rangeTo, hospitalId, sourceFilters]);

  const { data: report, isLoading } = useQuery({
    queryKey: ["lead-opd-report", params],
    queryFn: async () => {
      const res = await leadsApi.getOpdReport(params);
      return res.data.data as LeadOpdReport;
    },
  });

  const { data: drillRows = [], isLoading: drillLoading } = useQuery({
    queryKey: ["lead-opd-report-drill", drill, params],
    queryFn: async () => {
      const res = await leadsApi.getOpdReportDrill({
        ...params,
        metric: drill!.metric,
        staffId: drill!.staffId,
      });
      return (res.data.data?.items ?? []) as Array<{
        leadId: string;
        patientName: string;
        phone: string;
        source: string;
        leadStatus: string;
        leadSubStatus: string | null;
        staffName: string | null;
        mode: string;
        billedValue: number;
        bookedAt: string | null;
        visitedAt: string | null;
      }>;
    },
    enabled: !!drill,
  });

  const drillNavLeads = useMemo(
    () =>
      drillRows.map(
        (r) =>
          ({
            id: r.leadId,
            patientName: r.patientName,
            phone: r.phone,
            source: r.source,
            status: r.leadStatus,
            leadSubStatus: r.leadSubStatus,
          }) as Lead
      ),
    [drillRows]
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 p-3 border rounded-md bg-muted/30">
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input
            type="date"
            className="h-9 w-[10.5rem]"
            value={rangeFrom}
            onChange={(e) => setRangeFrom(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input
            type="date"
            className="h-9 w-[10.5rem]"
            value={rangeTo}
            onChange={(e) => setRangeTo(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Lead source</Label>
          <FilterMultiSelect
            values={sourceFilters}
            onValuesChange={setSourceFilters}
            options={sourceFilterOptions}
            placeholder="Source"
            allLabel="All sources"
            triggerClassName="w-[12rem]"
          />
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">OPD report</CardTitle>
          <p className="text-muted-foreground text-sm font-normal">
            Salesperson-wise OPD booked (online/offline, free/paid), OPD visited (reception confirmed
            in queue), and OPD status for free bookings (not visited, rescheduled, lost). Free = CRM
            free OPD or zero billed value. Click any count to open the underlying leads.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </p>
          ) : !report?.bySalesPerson?.length ? (
            <p className="text-muted-foreground text-sm">No OPD data for this period.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full min-w-[1100px] text-sm">
                <thead>
                  <tr className="border-b bg-muted/40 text-left">
                    <th className="p-2" rowSpan={2}>
                      Sales person
                    </th>
                    <th className="border-l p-2 text-center" colSpan={4}>
                      OPD booked
                    </th>
                    <th className="border-l p-2 text-center" colSpan={4}>
                      OPD visited
                    </th>
                    <th className="border-l p-2 text-center" colSpan={6}>
                      OPD status (free bookings)
                    </th>
                  </tr>
                  <tr className="border-b bg-muted/20 text-xs text-muted-foreground">
                    <th className="border-l p-2 text-right">On · Free</th>
                    <th className="p-2 text-right">On · Paid</th>
                    <th className="p-2 text-right">Off · Free</th>
                    <th className="p-2 text-right">Off · Paid</th>
                    <th className="border-l p-2 text-right">On · Free</th>
                    <th className="p-2 text-right">On · Paid</th>
                    <th className="p-2 text-right">Off · Free</th>
                    <th className="p-2 text-right">Off · Paid</th>
                    <th className="border-l p-2 text-right">On · No visit</th>
                    <th className="p-2 text-right">On · Resched</th>
                    <th className="p-2 text-right">On · Lost</th>
                    <th className="p-2 text-right">Off · No visit</th>
                    <th className="p-2 text-right">Off · Resched</th>
                    <th className="p-2 text-right">Off · Lost</th>
                  </tr>
                </thead>
                <tbody>
                  {report.bySalesPerson.map((row) => {
                    const repLabel =
                      row.staffId === EXITED_EMPLOYEES_BUCKET_ID
                        ? EXITED_EMPLOYEES_BUCKET_LABEL
                        : row.staffName;
                    const staffId =
                      row.staffId === EXITED_EMPLOYEES_BUCKET_ID ? row.staffId : row.staffId;
                    return (
                      <tr key={row.staffId} className="border-b">
                        <td className="p-2 font-medium">{repLabel}</td>
                        <SplitCells
                          split={row.booked}
                          prefix="booked"
                          staffId={staffId}
                          repLabel={repLabel}
                          onDrill={setDrill}
                        />
                        <SplitCells
                          split={row.visited}
                          prefix="visited"
                          staffId={staffId}
                          repLabel={repLabel}
                          onDrill={setDrill}
                        />
                        <td className="border-l p-2 text-right">
                          <CountCell
                            value={row.status.online.notVisited}
                            metric="status_online_not_visited"
                            staffId={staffId}
                            repLabel={repLabel}
                            onDrill={setDrill}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <CountCell
                            value={row.status.online.rescheduled}
                            metric="status_online_rescheduled"
                            staffId={staffId}
                            repLabel={repLabel}
                            onDrill={setDrill}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <CountCell
                            value={row.status.online.lost}
                            metric="status_online_lost"
                            staffId={staffId}
                            repLabel={repLabel}
                            onDrill={setDrill}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <CountCell
                            value={row.status.offline.notVisited}
                            metric="status_offline_not_visited"
                            staffId={staffId}
                            repLabel={repLabel}
                            onDrill={setDrill}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <CountCell
                            value={row.status.offline.rescheduled}
                            metric="status_offline_rescheduled"
                            staffId={staffId}
                            repLabel={repLabel}
                            onDrill={setDrill}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <CountCell
                            value={row.status.offline.lost}
                            metric="status_offline_lost"
                            staffId={staffId}
                            repLabel={repLabel}
                            onDrill={setDrill}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {report.totals && (
                  <tfoot>
                    <tr className="border-t-2 bg-muted/30 font-semibold">
                      <td className="p-2">Total</td>
                      <SplitCells
                        split={report.totals.booked}
                        prefix="booked"
                        repLabel="All reps"
                        onDrill={setDrill}
                      />
                      <SplitCells
                        split={report.totals.visited}
                        prefix="visited"
                        repLabel="All reps"
                        onDrill={setDrill}
                      />
                      <td className="border-l p-2 text-right">
                        <CountCell
                          value={report.totals.status.online.notVisited}
                          metric="status_online_not_visited"
                          repLabel="All reps"
                          onDrill={setDrill}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <CountCell
                          value={report.totals.status.online.rescheduled}
                          metric="status_online_rescheduled"
                          repLabel="All reps"
                          onDrill={setDrill}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <CountCell
                          value={report.totals.status.online.lost}
                          metric="status_online_lost"
                          repLabel="All reps"
                          onDrill={setDrill}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <CountCell
                          value={report.totals.status.offline.notVisited}
                          metric="status_offline_not_visited"
                          repLabel="All reps"
                          onDrill={setDrill}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <CountCell
                          value={report.totals.status.offline.rescheduled}
                          metric="status_offline_rescheduled"
                          repLabel="All reps"
                          onDrill={setDrill}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <CountCell
                          value={report.totals.status.offline.lost}
                          metric="status_offline_lost"
                          repLabel="All reps"
                          onDrill={setDrill}
                        />
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!drill}
        onOpenChange={(open) => {
          if (!open) {
            setDrill(null);
            setDetailLead(null);
          }
        }}
      >
        <DialogContent className="flex max-h-[min(88vh,720px)] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
            <DialogTitle>
              {drill
                ? `${METRIC_LABELS[drill.metric]} · ${drill.repLabel}`
                : "OPD drill-down"}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {drillLoading ? (
              <p className="text-muted-foreground flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </p>
            ) : drillRows.length === 0 ? (
              <p className="text-muted-foreground text-sm">No rows for this filter.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-2">Patient</th>
                    <th className="py-2 pr-2">Phone</th>
                    <th className="py-2 pr-2">Source</th>
                    <th className="py-2 pr-2">Status</th>
                    <th className="py-2 pr-2">Mode</th>
                    <th className="py-2 text-right">Billed ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {drillRows.map((row) => (
                    <tr
                      key={`${row.leadId}-${row.bookedAt ?? row.visitedAt ?? ""}`}
                      className="cursor-pointer border-b hover:bg-muted/40"
                      onClick={() =>
                        setDetailLead({
                          id: row.leadId,
                          patientName: row.patientName,
                          phone: row.phone,
                          source: row.source,
                          status: row.leadStatus,
                          leadSubStatus: row.leadSubStatus,
                        } as Lead)
                      }
                    >
                      <td className="py-2 pr-2 font-medium">{row.patientName}</td>
                      <td className="py-2 pr-2">
                        {maskPhone ? maskPhoneDisplay(row.phone) : row.phone}
                      </td>
                      <td className="py-2 pr-2">{row.source}</td>
                      <td className="py-2 pr-2">
                        {formatLeadStatusLabel(row.leadStatus as Lead["status"])}
                        {row.leadSubStatus
                          ? ` · ${formatLeadSubStatusLabel(row.leadSubStatus as Lead["leadSubStatus"])}`
                          : ""}
                      </td>
                      <td className="py-2 pr-2">{row.mode}</td>
                      <td className="py-2 text-right tabular-nums">
                        ₹{Number(row.billedValue || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <LeadDetailModal
        open={!!detailLead}
        onOpenChange={(open) => {
          if (!open) setDetailLead(null);
        }}
        anchorLead={detailLead}
        navLeads={drillNavLeads}
      />
    </div>
  );
}
